/**
 * useSourceHealth.ts
 *
 * Fetches source health stats + worker heartbeat for the Source Health Dashboard.
 * Both endpoints require SYSTEM_ADMIN.
 */
import { useState, useCallback } from 'react';
import { apiFetch } from '@/lib/apiFetch';

export interface SourceHealthRow {
  id: string;
  name: string;
  last_run_at: string | null;
  last_status: string | null;
  enabled: boolean;
  total: number;
  extracted: number;
  classified: number;
  embedded: number;
  not_searchable: number;
  base_url: string | null;
  crawl_config: Record<string, unknown> | null;
}

export interface WorkerHeartbeat {
  last_seen: string | null;
  minutes_ago: number;
  status: 'online' | 'offline';
  pid: number | null;
  jobs_active: number;
  hostname: string | null;
}

export function useSourceHealth() {
  const [sources, setSources] = useState<SourceHealthRow[]>([]);
  const [worker, setWorker] = useState<WorkerHeartbeat | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [healthRes, hbRes] = await Promise.all([
        apiFetch('/api/source-health'),
        apiFetch('/api/worker-heartbeat'),
      ]);

      if (!healthRes.ok) throw new Error(`source-health ${healthRes.status}`);
      if (!hbRes.ok) throw new Error(`worker-heartbeat ${hbRes.status}`);

      const [healthJson, hbJson] = await Promise.all([
        healthRes.json() as Promise<{ data: SourceHealthRow[] }>,
        hbRes.json() as Promise<{ data: WorkerHeartbeat }>,
      ]);

      setSources(healthJson.data ?? []);
      setWorker(hbJson.data ?? null);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  }, []);

  return { sources, worker, loading, error, lastRefreshed, refresh };
}
